import { useState } from 'react';
import { useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';

import useToast from '../../hooks/useToast';
import {
  getInspectors,
  getInspectorsEyM,
  getInspectorsEyMLegacy,
  postApprovers
} from '../../services/adminContracts';
import { ubicationTypeEnum } from '../../utils/dataStatus';

const filterMultipleApprovers = (approvers) => {
  // Initializes the Map by iterating over all approvers in the constructor.
  // If there are duplicate approvers, they will be overwritten for having the
  // same `id` key, and it is adapted to be displayed in the select.
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map
  const approversMap = new Map(
    approvers.map((approver) => [
      `${approver.id}-${approver.esGrupo ? 'grupo' : 'persona'}`,
      {
        esGrupo: approver.esGrupo,
        id: `${approver.id}-${approver.esGrupo ? 'grupo' : 'persona'}`,
        idOriginal: approver.id,
        nombre: approver.nombre,
        value: `${approver.id}-${approver.esGrupo ? 'grupo' : 'persona'}`,
        label: approver.nombre,
        idSolicitudServicioAprobador: approver?.idSolicitudServicioAprobador || 0,
        idProveedor: approver.idProveedor
      }
    ])
  );

  // Transforms `approversMap` back into an array
  return Array.from(approversMap, ([_, value]) => value);
};

const getEventId = (val, idEvento) => {
  const ubication = val?.ubication;
  const ubicationTypeIdLocation = ubication?.ubicationType?.id;
  const isWell = ubicationTypeEnum.pozo === ubicationTypeIdLocation;
  const isLocation = ubicationTypeEnum.locacion === ubicationTypeIdLocation;

  if (idEvento) {
    return idEvento;
  }

  if (isLocation) {
    return ubication?.typeEvent?.id || 0;
  }

  return isWell && ubication?.event?.id === 0 ? ubication?.operationType?.id : ubication?.event?.id;
};

const getHasUbicationTeam = (ubicationTeamValue) =>
  Array.isArray(ubicationTeamValue) ? ubicationTeamValue : [ubicationTeamValue];

const getImputationObjectHasMultipleWells = (imptationObjectWells) =>
  imptationObjectWells?.pozos ?? imptationObjectWells;

const getDataApproverByProvider = async ({
  providers,
  ubicationTeamValue,
  imptationObjectWells,
  ubicationTypeId,
  idEvento,
  values,
  serviceId,
  setNewToast
}) => {
  try {
    const hasUbicationTeam = getHasUbicationTeam(ubicationTeamValue);

    const imputationObjectHasMultipleWells =
      getImputationObjectHasMultipleWells(imptationObjectWells);
    const idEquipos = [
      ...new Set(
        ubicationTypeId === ubicationTypeEnum.pozo
          ? hasUbicationTeam
          : imputationObjectHasMultipleWells.map((item) => item.team?.value || item.equipo?.id)
      )
    ];

    const body = providers.length
      ? providers?.map((item) => {
          if (item.idUnidadNegocio && item.nroContrato) {
            return {
              nroContrato: item.nroContrato,
              idUnidadDeNegocio: item.idUnidadNegocio,
              idEvento: getEventId(values, idEvento),
              idTipoNegocio: 1
            };
          }
          if (!item.contract || !item.businessUnit) {
            // To validate so the service is not called afterward
            return null;
          }

          return {
            nroContrato: item?.contract?.nombre.split(' ')[0],
            idUnidadDeNegocio: item?.businessUnit?.id,
            idEvento: getEventId(values, idEvento),
            idTipoNegocio: 1
          };
        })
      : [];

    const newBody = {
      contratosEventos: body,
      idEquipos: idEquipos !== null ? idEquipos : [],
      idServicio: serviceId
    };

    const isSomeNull = body.some((b) => !b);

    // Here, postApproversContracts was replaced by postApprovers
    if (!isSomeNull && body.length) {
      const approversResponse = await postApprovers(newBody);

      return approversResponse;
    }

    return [];
  } catch (error) {
    setNewToast({
      type: 'ERROR',
      message: error.message
    });
    throw error;
  }
};

const getApproversOptions = async ({
  isOyM,
  contracts,
  requestValue,
  ubicationTeamValue,
  imptationObjectWells,
  ubicationTypeId,
  idEvento,
  values,
  serviceId,
  setNewToast,
  technicalLocationId,
  associatedUsersField,
  isCreateSDS,
  isCreateTemplateRequest,
  idEditDraft,
  setFieldValue,
  setApproverOptions,
  adapterOptionsFormik
}) => {
  let dataOptions = [];

  try {
    if (isOyM) {
      const body =
        contracts?.map((item) => ({
          nroContrato: item.nroContrato,
          idUnidadDeNegocio: item.idOperacion || item.idUnidadNegocio,
          idTipoNegocio: 2,
          idEvento: 0
        })) ||
        requestValue?.provider?.providers.map(({ contract }) => ({
          nroContrato: contract?.nombre,
          idUnidadDeNegocio: values?.ubication?.uunn?.id,
          idTipoNegocio: 2,
          idEvento: 0
        }));

      const newApproverBodyRequest = {
        contratosEventos: body,
        idServicio: serviceId,
        idEquipos: [],
        idUbicacionesTecnicas: [technicalLocationId || values?.ubication?.ubication.id]
      };

      // Here, getApproversOyM was replaced by postApprovers
      dataOptions = await postApprovers(newApproverBodyRequest);
    }

    if (!isOyM) {
      if (requestValue?.provider?.providers) {
        const dataApprover = await getDataApproverByProvider({
          providers: requestValue?.provider?.providers,
          ubicationTeamValue,
          imptationObjectWells,
          ubicationTypeId,
          idEvento,
          values,
          serviceId,
          setNewToast
        });

        dataOptions = [...dataApprover];
      } else if (contracts.length) {
        const dataApprover = await getDataApproverByProvider({
          providers: contracts,
          ubicationTeamValue,
          imptationObjectWells,
          ubicationTypeId,
          idEvento,
          values,
          serviceId,
          setNewToast
        });

        dataOptions = [...dataOptions, ...dataApprover];
      }
    }

    const adapterOptionAppr = filterMultipleApprovers(dataOptions);

    if (isCreateSDS || isCreateTemplateRequest || idEditDraft) {
      setFieldValue(`${associatedUsersField}.approvers`, adapterOptionsFormik(adapterOptionAppr));
    }

    setApproverOptions(adapterOptionAppr);
  } catch (err) {
    setNewToast({
      type: 'ERROR',
      message: err.message
    });
  }
};

const useAssociatedUsersForm = (props) => {
  const {
    requestValue,
    serviceId,
    ubicationTypeId,
    ubicationTeamValue = [],
    imptationObjectWells,
    associatedUsersValue,
    associatedUsersField,
    contracts,
    idEvento = null,
    technicalLocationId,
    isOyM: isOyMProp
  } = props;

  const history = useHistory();
  const isCreateSDS = history.location.pathname.includes('createMultiple');
  const isCreateTemplateRequest = history.location.pathname.includes('generate-template/request');
  const isEditTemplateRequest = history.location.pathname.includes('edit-template/request');

  const idEditDraft = history.location.pathname.includes('edit');
  const [approverOptions, setApproverOptions] = useState([]);
  const [inspectorsOptions, setInspectorsOptions] = useState([]);

  const { setFieldValue, values } = useFormikContext();
  const { setNewToast } = useToast();

  const isOyM = typeof isOyMProp === 'boolean' ? isOyMProp : !!values?.isOyM;

  const adapterOptionsFormik = (options) =>
    options.map((option) => ({
      ...option,
      id: option.idOriginal
    }));

  const getOptionApprover = async () =>
    getApproversOptions({
      isOyM,
      contracts,
      requestValue,
      ubicationTeamValue,
      imptationObjectWells,
      ubicationTypeId,
      idEvento,
      values,
      serviceId,
      setNewToast,
      technicalLocationId,
      associatedUsersField,
      isCreateSDS,
      isCreateTemplateRequest,
      idEditDraft,
      setFieldValue,
      setApproverOptions,
      adapterOptionsFormik
    });

  const getInspectorsOptions = async () => {
    const key = isOyM ? 'operacionId' : 'unidadNegocioId';
    const inspectorBodyRequest = requestValue.provider.providers.map((provider) => ({
      eventoId: getEventId(values, idEvento),
      nroContrato: provider.contract.nombre.split(' ')[0],
      [key]: values.ubication.uunn.id
    }));

    try {
      const getInspectorWhenIsCreateSDS = () =>
        isCreateSDS || isCreateTemplateRequest || isEditTemplateRequest
          ? getInspectorsEyM(inspectorBodyRequest)
          : getInspectorsEyMLegacy(inspectorBodyRequest);

      const inspectorsList = values.isOyM
        ? await getInspectors(inspectorBodyRequest)
        : await getInspectorWhenIsCreateSDS();

      setInspectorsOptions(inspectorsList);

      if (!associatedUsersValue?.inspectors?.length) {
        setFieldValue(`${associatedUsersField}.inspectors`, inspectorsList);

        return;
      }
      setFieldValue(`${associatedUsersField}.inspectors`, associatedUsersValue?.inspectors);
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
    }
  };

  return {
    approverOptions,
    filterMultipleApprovers,
    adapterOptionsFormik,
    getOptionApprover,
    inspectorsOptions,
    getInspectorsOptions
  };
};

export default useAssociatedUsersForm;
